import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import type { AuthContextType, AuthUser, UserRole } from '../types/auth.types'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

/**
 * AuthProvider manages authentication state and provides auth actions.
 * Extracts firm_id and user_role from JWT custom claims.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [firmId, setFirmId] = useState<string | null>(null)
  const [clientId, setClientId] = useState<string | null>(null)
  const [userRole, setUserRole] = useState<UserRole | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const loadRoleContextFromDb = useCallback(async (_userId: string) => {
    // Use SECURITY DEFINER RPCs — direct user_roles SELECT is restricted to partners only
    const [firmResult, roleResult] = await Promise.all([
      supabase.rpc('get_user_firm_id'),
      supabase.rpc('get_user_role'),
    ])

    if (firmResult.error) {
      console.error('Failed to load firm_id from DB:', firmResult.error.message)
    }
    if (roleResult.error) {
      console.error('Failed to load role from DB:', roleResult.error.message)
    }

    if (firmResult.data) setFirmId(firmResult.data)
    if (roleResult.data) setUserRole(roleResult.data as UserRole)
  }, [])

  /** Extract custom claims from JWT token */
  const extractClaims = useCallback(async (session: Session | null) => {
    if (!session?.access_token) {
      setFirmId(null)
      setClientId(null)
      setUserRole(null)
      return
    }

    try {
      // Decode JWT payload (base64)
      const payload = session.access_token.split('.')[1]
      const decoded = JSON.parse(atob(payload))

      const tokenFirmId = decoded.firm_id || null
      const tokenUserRole = decoded.user_role || null
      const metadataFirmId = (session.user?.app_metadata as Record<string, unknown> | undefined)?.firm_id as string | undefined
      const metadataUserRole = (session.user?.app_metadata as Record<string, unknown> | undefined)?.user_role as UserRole | undefined
      const effectiveFirmId = tokenFirmId || metadataFirmId || null
      const effectiveUserRole = tokenUserRole || metadataUserRole || null

      setFirmId(effectiveFirmId)
      setClientId(decoded.client_id || null)
      setUserRole(effectiveUserRole)

      // Fallback for environments where custom JWT claims are not configured yet.
      if ((!effectiveFirmId || !effectiveUserRole) && session.user?.id) {
        await loadRoleContextFromDb(session.user.id)
      }
    } catch {
      console.error('Failed to decode JWT claims')
      setClientId(null)

      if (session.user?.id) {
        await loadRoleContextFromDb(session.user.id)
      } else {
        setFirmId(null)
        setUserRole(null)
      }
    }
  }, [loadRoleContextFromDb])

  /** Initialize auth state on mount */
  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        setSession(session)
        setUser(session?.user as AuthUser || null)
        await extractClaims(session)
      } catch (error) {
        console.error('Auth initialization failed:', error)
      } finally {
        setIsLoading(false)
      }
    }

    initAuth()

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session)
        setUser(session?.user as AuthUser || null)
        await extractClaims(session)
        setIsLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [extractClaims])

  /** Sign in with email and password */
  const signIn = useCallback(async (email: string, password: string) => {
    setIsLoading(true)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }, [])

  /** Create a new account (invitation-based sign-up) */
  const signUp = useCallback(async (email: string, password: string, redirectTo?: string) => {
    const { error } = await supabase.auth.signUp({
      email: email.toLowerCase().trim(),
      password,
      options: { emailRedirectTo: redirectTo ?? `${window.location.origin}/dashboard` },
    })
    if (error) throw error
  }, [])

  /** Sign in with magic link (passwordless) */
  const signInWithMagicLink = useCallback(async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    })
    if (error) throw error
  }, [])

  /** Send a password reset email */
  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`
    })
    if (error) throw error
  }, [])

  /** Update the current user's password (used after a reset link, or when logged in) */
  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password })
    if (error) throw error
  }, [])

  /** Sign out current user */
  const signOut = useCallback(async () => {
    // Clear local state first so the UI redirects immediately,
    // regardless of whether the Supabase server call succeeds.
    setUser(null)
    setSession(null)
    setFirmId(null)
    setClientId(null)
    setUserRole(null)
    await supabase.auth.signOut()
  }, [])

  /** Manually refresh session */
  const refreshSession = useCallback(async () => {
    const { data: { session }, error } = await supabase.auth.refreshSession()
    if (error) throw error
    setSession(session)
    await extractClaims(session)
  }, [extractClaims])

  const value: AuthContextType = {
    user,
    session,
    firmId,
    clientId,
    userRole,
    isLoading,
    isAuthenticated: !!user,
    isClient: userRole === 'client',
    signIn,
    signUp,
    signInWithMagicLink,
    resetPassword,
    updatePassword,
    signOut,
    refreshSession
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/** Hook to access auth context */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
